import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Text, Stack } from 'basis';
import { LayoutPage, LayoutSection } from 'components';
import { useAppContext, useClaimApplication, useGetApplication, useTrackingError } from 'hooks';
import { ApiError, Routes } from 'types';
import { patch } from 'context';
import { resolveAssistedApplyRoute } from 'utils/resolveAssistedApplyRoute';

export const AssistedApplyOrchestrator: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useAppContext();
  const { trackError } = useTrackingError();
  const hasStarted = useRef(false);

  const onClaimError = (error: ApiError) => {
    trackError({ error });
    history.replace(Routes.assistedApplyClaimError);
  };

  const onGetApplicationError = (error: ApiError) => {
    trackError({ error });
    history.replace(Routes.assistedApplyClaimError);
  };

  const { claimApplication } = useClaimApplication({ onError: onClaimError });
  const { getApplication } = useGetApplication({ onError: onGetApplicationError });

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const orchestrate = async () => {
      const externalApplicationId = state.appNum;
      if (!externalApplicationId) {
        history.replace(Routes.assistedApplyClaimError);
        return;
      }

      // Step 1: Claim the application
      const claimResult = await claimApplication(externalApplicationId);
      if (!claimResult) return; // error handled by onClaimError

      // Step 2: Store the applicationId
      dispatch(patch({ assistedApplyApplicationId: claimResult.applicationId }));

      // Step 3: Fetch application details
      const appResult = await getApplication(claimResult.applicationId);
      if (!appResult) return; // error handled by onGetApplicationError

      // Step 4: Route based on status
      const route = resolveAssistedApplyRoute(
        appResult.status,
        appResult.detailedStatus?.details?.reason,
      );
      history.replace(route);
    };

    orchestrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LayoutPage step="update">
      <LayoutSection>
        <Stack gap="8">
          <Text textStyle="heading5">Processing your application...</Text>
          <Text>Please wait while we retrieve your application details.</Text>
        </Stack>
      </LayoutSection>
    </LayoutPage>
  );
};
